import React, { useState } from "react";
import { Container, Row, Col, Card, Collapse } from "reactstrap";
import { Link, useNavigate } from "react-router-dom";
import Dropzone from "react-dropzone";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import { useEffect, useRef } from "react";
import classnames from "classnames";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { base_url } from "../../constants";

const AddQuestionCourse = () => {
  document.title = "Add Question Course | Medicotoon";
  const navigate = useNavigate();
  const localdata = localStorage.getItem("elmatary_admin");
  let adminData = localdata && JSON.parse(localdata);

  const [universities, setUniversities] = useState([]);
  const [grades, setGrades] = useState([]);
  const [selectedUni, setSelectedUni] = useState("");
  const [selectedGrade, setSelectedGrade] = useState("");
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [coursePhotoUrl, setCoursePhotoUrl] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const courseFormRef = useRef();

  const getUniversities = async () => {
    try {
      const data_send = {
        admin_id: adminData.admin_id,
        access_token: adminData.access_token,
      };
      const res = await axios.post(
        `${base_url}/admin/universities/select_universities_grade.php`,
        JSON.stringify(data_send)
      );
      if (res.status === "success" && Array.isArray(res.message)) {
        setUniversities(res.message);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleUniversityChange = (uniId) => {
    setSelectedUni(uniId);
    setSelectedGrade("");
    const foundUni = universities.find((u) => String(u.university_id) === String(uniId));
    if (foundUni && Array.isArray(foundUni.grades)) {
      setGrades(foundUni.grades);
    } else {
      setGrades([]);
    }
  };

  useEffect(() => {
    getUniversities();
  }, []);

  const handleAcceptedFiles = async (files) => {
    if (!files.length) return;
    const file = files[0];
    const formData = new FormData();
    formData.append("image", file);

    try {
      const res = await axios.post(
        `${base_url}/admin/image_uploader.php`,
        formData
      );
      if (res) {
        setCoursePhotoUrl(res);
        toast.success("Cover image uploaded successfully");
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to upload cover image");
    }
  };

  const handleAddCourse = async (e) => {
    e.preventDefault();
    const form = courseFormRef.current;
    const name = form.course_name.value.trim();
    const price = form.course_price.value.trim();
    const content = form.course_content.value.trim();

    if (!name || !price || !selectedUni || !selectedGrade) {
      toast.warn("Please fill all required fields");
      return;
    }

    setIsSubmitting(true);
    try {
      const data_send = {
        course_name: name,
        course_price: price,
        course_photo_url: coursePhotoUrl,
        course_content: content || "Question Course",
        category_id: "0",
        course_type: "questions",
        university_id: selectedUni,
        grade_id: selectedGrade,
        admin_id: adminData.admin_id,
        access_token: adminData.access_token,
        free: 0,
        show_in_website: 1,
        show_in_mobile: 1,
      };

      const res = await axios.post(
        `${base_url}/admin/courses/add_course.php`,
        JSON.stringify(data_send)
      );

      if (res.status === "success") {
        toast.success("Question Course added successfully");
        navigate("/question-courses");
      } else {
        toast.error(res.message || "Failed to add course");
      }
    } catch (err) {
      console.error(err);
      toast.error("Server connection error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid={true}>
          <Breadcrumbs
            title="Question Courses"
            breadcrumbItem="Add Question Course"
          />

          <Row>
            <Col lg={12}>
              <Card className="shadow-sm">
                <div className="p-4 border-bottom">
                  <h5 className="font-size-16 mb-1 text-primary">
                    <i className="bx bx-plus-circle me-1"></i> Add New Question
                    Course
                  </h5>
                  <p className="text-muted mb-0">
                    Fill in the course details below. Questions can be assigned
                    to units within this course.
                  </p>
                </div>

                <div className="p-4">
                  <form ref={courseFormRef} onSubmit={handleAddCourse}>
                    <Row>
                      <Col md={6}>
                        <div className="mb-3">
                          <label className="form-label fw-semibold">
                            Course Name *
                          </label>
                          <input
                            name="course_name"
                            placeholder="e.g. Immunology Questions Bank"
                            type="text"
                            className="form-control"
                            required
                          />
                        </div>
                      </Col>

                      <Col md={6}>
                        <div className="mb-3">
                          <label className="form-label fw-semibold">
                            Price (JOD) *
                          </label>
                          <input
                            name="course_price"
                            placeholder="e.g. 150"
                            type="number"
                            className="form-control"
                            required
                          />
                        </div>
                      </Col>
                    </Row>

                    <Row>
                      <Col md={6}>
                        <div className="mb-3">
                          <label className="form-label fw-semibold">
                            Select University *
                          </label>
                          <select
                            className="form-select"
                            value={selectedUni}
                            onChange={(e) => handleUniversityChange(e.target.value)}
                            required
                          >
                            <option value="">-- Choose University --</option>
                            {universities.map((uni) => (
                              <option
                                key={uni.university_id}
                                value={uni.university_id}
                              >
                                {uni.university_name}
                              </option>
                            ))}
                          </select>
                        </div>
                      </Col>

                      <Col md={6}>
                        <div className="mb-3">
                          <label className="form-label fw-semibold">
                            Select Grade/Year *
                          </label>
                          <select
                            className="form-select"
                            value={selectedGrade}
                            disabled={!selectedUni}
                            onChange={(e) => setSelectedGrade(e.target.value)}
                            required
                          >
                            <option value="">-- Choose Grade --</option>
                            {grades.map((g) => (
                              <option key={g.grade_id} value={g.grade_id}>
                                {g.grade_name}
                              </option>
                            ))}
                          </select>
                        </div>
                      </Col>
                    </Row>

                    <div className="mb-3">
                      <label className="form-label fw-semibold">
                        Course Description / Notes
                      </label>
                      <textarea
                        name="course_content"
                        rows="3"
                        placeholder="Brief overview about this question course..."
                        className="form-control"
                      ></textarea>
                    </div>

                    <div className="mb-4">
                      <label className="form-label fw-semibold">
                        Course Cover Image
                      </label>
                      <Dropzone
                        onDrop={(acceptedFiles) =>
                          handleAcceptedFiles(acceptedFiles)
                        }
                      >
                        {({ getRootProps, getInputProps }) => (
                          <div className="dropzone border-dashed p-4 text-center rounded bg-light">
                            <div
                              className="dz-message needsclick"
                              {...getRootProps()}
                              style={{ cursor: "pointer" }}
                            >
                              <input {...getInputProps()} />
                              <div className="mb-2">
                                <i className="display-4 text-muted bx bx-cloud-upload" />
                              </div>
                              <h5>Drop course cover here or click to upload</h5>
                            </div>
                          </div>
                        )}
                      </Dropzone>

                      {coursePhotoUrl && (
                        <div className="mt-3">
                          <img
                            src={coursePhotoUrl}
                            alt="Course Preview"
                            style={{
                              maxHeight: "150px",
                              borderRadius: "8px",
                              border: "1px solid #ddd",
                            }}
                          />
                        </div>
                      )}
                    </div>

                    <div className="d-flex justify-content-end gap-2">
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => navigate("/question-courses")}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="btn btn-success px-4"
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? "Creating..." : "Create Question Course"}
                      </button>
                    </div>
                  </form>
                </div>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
      <ToastContainer />
    </React.Fragment>
  );
};

export default AddQuestionCourse;
